import { create, show, useModal } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";

import Button from "ds/components/Button";
import ModalCancelButton from "ds/components/Modal/CancelButton";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import ModalHeader from "ds/components/Modal/Header";
import ModalSimple from "ds/components/ModalSimple";
import Typography from "ds/components/Typography";
import FormFieldTextConfirm from "components/FormFields/Confirm";
import useSpaceliftLoginsCookie from "hooks/useSpaceliftLoginsCookie";
import useTypedContext from "hooks/useTypedContext";
import setLocation from "shared/setLocation";
import { AccountContext } from "views/AccountWrapper";
import { createAccountNameWithRegion, getRegion } from "utils/region";

import { useConfirmAccountDelete } from "../useAccountDeletion/mutations";
import { DELETION_CONFIRMATION_TEXT } from "./constants";

type FormData = {
  deleteConfirmationText: string;
};

const DeleteConfirmationModal = create(function DeleteConfirmationModal() {
  const modal = useModal();
  const { accountName } = useTypedContext(AccountContext);
  const { cookieLoginsWithRegion, setCookieLogins } = useSpaceliftLoginsCookie();

  const [confirmAccountDelete] = useConfirmAccountDelete();

  const form = useForm<FormData>({
    defaultValues: {
      deleteConfirmationText: "",
    },
    mode: "onChange",
  });

  const { handleSubmit, formState } = form;

  const region = getRegion();
  const removeDeletedAccountFromCookies = () => {

    const filteredLogins = cookieLoginsWithRegion.filter(
      (item) => {
        if (item.accountName === accountName && item.region === region) {
          return false;
        }

        return true;
      }
    );


    setCookieLogins(
      filteredLogins.map((item) => createAccountNameWithRegion(item.accountName, item.region))
    );
  };

  const onSubmit = async () => {
    await confirmAccountDelete({}, (result) => {
      if (result?.accountConfirmDelete) {
        removeDeletedAccountFromCookies();
        setLocation("https://spacelift.io");
      }
    });
  };

  return (
    <ModalSimple>
      <ModalHeader title="Delete account" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...form}>
          <ModalContent gap="x-large">
            <Typography variant="p-body3" tag="p">
              This will permanently delete this account and all associated user accounts, resources,
              and configuration data. This action is irreversible and cannot be undone.
            </Typography>

            <FormFieldTextConfirm
              fieldName="deleteConfirmationText"
              textToConfirm={DELETION_CONFIRMATION_TEXT}
            />
          </ModalContent>

          <ModalFooter>
            <ModalCancelButton onClick={modal.hide} />
            <Button
              variant="dangerPrimary"
              type="submit"
              loading={formState.isSubmitting}
              disabled={!formState.isValid || formState.isSubmitting}
            >
              Delete
            </Button>
          </ModalFooter>
        </FormProvider>
      </form>
    </ModalSimple>
  );
});

export const showDeleteConfirmationModal = () => show(DeleteConfirmationModal);
